/**
 * useSystemCommonCode Hook
 * 공통코드 관리 훅
 *
 * @description
 * - 공통코드 그룹 및 아이템 조회
 * - 그룹별 공통코드 캐싱 (불필요한 API 호출 방지)
 * - 아코디언 방식 펼침/접힘 상태 관리
 * - 페이지 진입 시 전체 코드 미리 로드
 */
import { useState, useEffect, useCallback } from "react";
import { commonCodeApi } from "../api/systemCommonCodeApi";
import { CommonCodeGroup, CommonCodeItem } from "../types/systemCommonCode.types";
import {
    extractCodesFromResponse,
    extractGroupsFromResponse,
    extractGroupsFromCodes,
    sortCodes,
} from "../../shared/lib/commonCodeParserHelpers";

/**
 * 공통코드 관리 훅
 * @returns {Object} 공통코드 관련 상태 및 함수
 * @property {CommonCodeGroup[]} groups - 그룹 목록
 * @property {boolean} groupsLoading - 그룹 로딩 상태
 * @property {boolean} groupsExpanded - 그룹 목록 펼침 상태
 * @property {Set<string>} expandedGroupCodes - 펼쳐진 그룹 코드 Set
 * @property {Record<string, CommonCodeItem[]>} groupCodesCache - 그룹별 공통코드 캐시
 * @property {Record<string, boolean>} groupCodesLoading - 그룹별 로딩 상태
 * @property {Function} fetchGroups - 그룹 목록 펼치기/접기 함수
 * @property {Function} toggleGroupCodes - 그룹별 공통코드 토글 함수
 */
export const useSystemCommonCode = () => {
    // ======== 상태 관리 ========

    /** 그룹 목록 (vr_comm_grp) */
    const [groups, setGroups] = useState<CommonCodeGroup[]>([]);
    /** 그룹 로딩 상태 */
    const [groupsLoading, setGroupsLoading] = useState(false);
    /** 그룹 목록 펼침 상태 */
    const [groupsExpanded, setGroupsExpanded] = useState(false);
    /** 펼쳐진 그룹 코드 Set (아코디언) */
    const [expandedGroupCodes, setExpandedGroupCodes] = useState<Set<string>>(new Set());
    /** 그룹별 공통코드 캐시 */
    const [groupCodesCache, setGroupCodesCache] = useState<Record<string, CommonCodeItem[]>>({});
    /** 그룹별 로딩 상태 */
    const [groupCodesLoading, setGroupCodesLoading] = useState<Record<string, boolean>>({});
    /** 전체 공통코드 캐시 */
    const [allCodesCache, setAllCodesCache] = useState<CommonCodeItem[]>([]);

    // ======== API 호출 ========

    /**
     * 페이지 진입 시 공통코드 API 호출
     * 전체 코드와 그룹 목록을 캐시에 저장
     */
    useEffect(() => {
        const fetchCommonCode = async () => {
            try {
                const response = await commonCodeApi.getCommonCode();
                const codes = extractCodesFromResponse(response);
                setAllCodesCache(codes);

                // groups 배열 추출 시도, 없으면 codes에서 추출
                let uniqueGroups = extractGroupsFromResponse(response);
                if (uniqueGroups.length === 0) {
                    uniqueGroups = extractGroupsFromCodes(codes);
                }
                setGroups(uniqueGroups);
            } catch {
                // 에러 시 조용히 실패 (초기 로딩이므로)
            }
        };

        fetchCommonCode();
    }, []);

    // ======== 그룹 관련 핸들러 ========

    /**
     * 공통코드 그룹 목록 펼치기/접기
     */
    const fetchGroups = useCallback(async () => {
        // 이미 펼쳐져 있으면 접기
        if (groupsExpanded && groups.length > 0) {
            setGroupsExpanded(false);
            setExpandedGroupCodes(new Set());
            return;
        }

        // 이미 그룹 목록이 있으면 그대로 표시
        if (groups.length > 0) {
            setGroupsExpanded(true);
            setExpandedGroupCodes(new Set());
            return;
        }

        // 그룹 목록 조회
        setGroupsLoading(true);
        try {
            const response = await commonCodeApi.getCommonCode();
            let groupsData = extractGroupsFromResponse(response);

            if (groupsData.length === 0) {
                const codes = extractCodesFromResponse(response);
                groupsData = extractGroupsFromCodes(codes);
            }

            setGroups(groupsData);
            setGroupsExpanded(true);
            setExpandedGroupCodes(new Set());
        } catch {
            // 에러 처리
        } finally {
            setGroupsLoading(false);
        }
    }, [groupsExpanded, groups.length]);

    /**
     * 그룹별 공통코드 토글 (아코디언 방식)
     * @param grpCode 그룹 코드
     */
    const toggleGroupCodes = useCallback(async (grpCode: string) => {
        const isExpanded = expandedGroupCodes.has(grpCode);

        if (isExpanded) {
            // 접기
            setExpandedGroupCodes(prev => {
                const newSet = new Set(prev);
                newSet.delete(grpCode);
                return newSet;
            });
            return;
        }

        // 펼치기
        setExpandedGroupCodes(prev => new Set(prev).add(grpCode));

        // 이미 캐시에 있으면 스킵
        if (groupCodesCache[grpCode]) return;

        // 로딩 및 캐시 저장
        setGroupCodesLoading(prev => ({ ...prev, [grpCode]: true }));

        const filteredCodes = allCodesCache.filter(code => code.grpCode === grpCode);
        const sortedCodes = sortCodes(filteredCodes);

        setGroupCodesCache(prev => ({ ...prev, [grpCode]: sortedCodes }));
        setGroupCodesLoading(prev => ({ ...prev, [grpCode]: false }));
    }, [expandedGroupCodes, groupCodesCache, allCodesCache]);

    return {
        // 그룹 관련
        groups,
        groupsLoading,
        groupsExpanded,
        fetchGroups,

        // 그룹별 공통코드 (아코디언)
        expandedGroupCodes,
        groupCodesCache,
        groupCodesLoading,
        toggleGroupCodes,
    };
};
